/** @jsxRuntime classic */
/** @jsx jsx */

import { css, jsx } from '@emotion/react';
import { labelStyles } from '@zapier/style-encapsulation';
import { ArrowLink } from '../ArrowLink/ArrowLink';
import { RichTextHtml } from '../RichTextHtml/RichTextHtml';
import { RichTextHTMLString } from '../sections/types';
import { Colors, Typography } from '@zapier/design-system';
import { MEDIA_MIN_WIDTH } from '../../constants';
import { CTAType, SectionName, useAvroTracking } from '../../observability';

export type Props = {
  heading: string;
  description: RichTextHTMLString;
  features: {
    heading: string;
    description: string;
    href: string;
    linkText: string;
  }[];
};

const Styles = labelStyles('TwoColumnFeaturesSection', {
  root: css({
    display: 'grid',
    gridGap: '50px',
    margin: 'auto',
    maxWidth: '1170px',
    padding: '48px 20px',

    [MEDIA_MIN_WIDTH.small]: {
      gridGap: '48px',
    },

    [MEDIA_MIN_WIDTH.large]: {
      gridGap: '104px',
      gridTemplateColumns: '1fr 1fr',
      padding: '96px 20px 120px 20px',
    },
  }),
  sectionContent: css({
    [MEDIA_MIN_WIDTH.large]: {
      width: '90%',
    },
  }),
  sectionHeading: css({
    color: Colors.earth,
    fontSize: '36px',
    fontWeight: 600,
    letterSpacing: '1px',
    lineHeight: '110%',
    paddingBottom: '20px',
    ...Typography.heading,

    [MEDIA_MIN_WIDTH.small]: {
      fontSize: '40px',
      letterSpacing: '1.5px',
      lineHeight: '100%',
      paddingBottom: '39px',
    },

    [MEDIA_MIN_WIDTH.large]: {
      fontSize: '60px',
      lineHeight: '110%',
      paddingBottom: '27px',
    },
  }),
  sectionDescription: css({
    color: Colors.earth,
    fontSize: '20px',
    fontWeight: 400,
    letterSpacing: '-0.5px',
    lineHeight: '140%',
    ...Typography.base,

    [MEDIA_MIN_WIDTH.small]: {
      fontSize: '24px',
      lineHeight: '133%',
    },

    [MEDIA_MIN_WIDTH.large]: {
      fontSize: '20px',
      letterSpacing: '-0.5px',
      lineHeight: '140%',
    },
  }),
  features: css({
    display: 'grid',
    gridGap: '50px',

    [MEDIA_MIN_WIDTH.small]: {
      gridGap: '48px',
      width: '90%',
    },

    [MEDIA_MIN_WIDTH.large]: {
      gridGap: '63px',
      width: '87%',
    },
  }),
  featureHeading: css({
    color: Colors.earth,
    fontSize: '19px',
    fontWeight: 700,
    lineHeight: '121%',
    paddingBottom: '10px',
    ...Typography.base,

    [MEDIA_MIN_WIDTH.small]: {
      fontSize: '24px',
      lineHeight: '133%',
    },

    [MEDIA_MIN_WIDTH.large]: {
      fontSize: '26px',
      letterSpacing: '-0.5%',
      lineHeight: '120%',
      paddingBottom: '24px',
    },
  }),
  featureDescription: css({
    color: Colors.earth,
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '150%',
    paddingBottom: '20px',
    ...Typography.base,

    [MEDIA_MIN_WIDTH.small]: {
      fontSize: '18px',
      lineHeight: '133%',
    },
  }),
});

export function TwoColumnFeaturesSection({
  heading,
  description,
  features,
}: Props) {
  const { emitCTAClickEvent } = useAvroTracking();
  return (
    <div css={Styles.root}>
      <div css={Styles.sectionContent}>
        <h2 css={Styles.sectionHeading}>{heading}</h2>
        <div css={Styles.sectionDescription}>
          <RichTextHtml html={description} />
        </div>
      </div>
      <div css={Styles.features}>
        {features?.map((feature) => (
          <div key={`${feature.heading}`}>
            <h3 css={Styles.featureHeading}>{feature.heading}</h3>
            <p css={Styles.featureDescription}>
              <RichTextHtml html={feature.description} />
            </p>
            {feature.linkText && (
              <ArrowLink
                href={feature.href}
                onClick={() =>
                  emitCTAClickEvent({
                    text: feature.linkText,
                    href: feature.href,
                    type: CTAType.cta_link,
                    section: SectionName.TwoColumnSection,
                  })
                }
              >
                {feature.linkText}
              </ArrowLink>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
